// CitySearchBar.js (only the loading bits changed)
import { useEffect, useState, useRef } from "react";
import manualCities from "./manualCities";

const MIN_CHARS = 2;
const MAX_RESULTS = 12;

const CitySearchBar = ({ onSelectLocation }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const worldCitiesRef = useRef(null); // loaded once on demand

  const rapidApiKey = process.env.REACT_APP_RAPIDAPI_KEY || "";

  // 🔹 Load from src/data/worldcities.min.json lazily
  const ensureWorldCitiesLoaded = async () => {
    if (worldCitiesRef.current) return;
    try {
      const mod = await import("./data/worldcities.min.json"); // <-- from src
      worldCitiesRef.current = mod.default;
    } catch (e) {
      console.warn("Could not load local worldcities dataset:", e);
      worldCitiesRef.current = [];
    }
  };

  useEffect(() => {
    const run = async () => {
      const term = searchTerm.trim();
      if (term.length < MIN_CHARS) {
        setResults([]);
        setActiveIdx(-1);
        return;
      }

      setLoading(true);

      // 1) Manual first
      const manual = manualCities
        .filter((c) => c.name.toLowerCase().includes(term.toLowerCase()))
        .slice(0, MAX_RESULTS);
      if (manual.length) {
        setResults(manual);
        setLoading(false);
        setActiveIdx(-1);
        return;
      }

      // 2) Local world dataset
      await ensureWorldCitiesLoaded();
      const world = worldCitiesRef.current || [];
      if (world.length) {
        const hits = [];
        const t = term.toLowerCase();
        for (let i = 0; i < world.length && hits.length < MAX_RESULTS; i++) {
          const w = world[i];
          if (
            (w.name && w.name.toLowerCase().includes(t)) ||
            (w.country && w.country.toLowerCase().includes(t))
          ) {
            hits.push({
              name: `${w.name}, ${w.country}`,
              lat: w.lat,
              lng: w.lng,
            });
          }
        }
        if (hits.length) {
          setResults(hits);
          setLoading(false);
          setActiveIdx(-1);
          return;
        }
      }

      // 3) Optional API fallback (only if you added the key)
      if (rapidApiKey) {
        try {
          const limit = MAX_RESULTS;
          const url = `https://wft-geo-db.p.rapidapi.com/v1/geo/cities?limit=${limit}&namePrefix=${encodeURIComponent(
            term
          )}`;
          const res = await fetch(url, {
            headers: {
              "X-RapidAPI-Key": rapidApiKey,
              "X-RapidAPI-Host": "wft-geo-db.p.rapidapi.com",
            },
          });
          const data = await res.json();
          const formatted = (data?.data || []).map((city) => ({
            name: `${city.name}, ${city.countryCode}`,
            lat: city.latitude,
            lng: city.longitude,
          }));
          setResults(
            formatted.length
              ? formatted
              : [{ name: "No results", disabled: true }]
          );
        } catch {
          setResults([{ name: "No results", disabled: true }]);
        } finally {
          setLoading(false);
          setActiveIdx(-1);
        }
        return;
      }

      setResults([{ name: "No results", disabled: true }]);
      setLoading(false);
      setActiveIdx(-1);
    };

    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [searchTerm, rapidApiKey]);

  const choose = (item) => {
    if (item.disabled) return;
    onSelectLocation(item);
    setSearchTerm("");
    setResults([]);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      choose(results[activeIdx]);
    }
  };

  return (
    <div
      style={{ position: "relative", zIndex: 25, width: "100%", maxWidth: 350 }}
    >
      <input
        type="text"
        placeholder="Search city..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={onKeyDown}
        style={{
          marginTop: 8,
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #2a2a35",
          // width: 280,
          outline: "none",
          background: "rgba(18,18,24,0.9)",
          color: "white",
        }}
      />
      {(loading ||
        (results.length > 0 && searchTerm.trim().length >= MIN_CHARS)) && (
        <div
          style={{
            // position: "absolute",
            background: "rgba(18,18,24,0.95)",
            border: "1px solid #2a2a35",
            // width: 280,
            maxHeight: 260,
            overflowY: "auto",
            marginTop: 6,
            borderRadius: 8,
            color: "white",
          }}
        >
          {loading && (
            <div style={{ padding: "10px 12px", opacity: 0.7 }}>Searching…</div>
          )}
          {!loading &&
            results.map((item, idx) => (
              <div
                key={idx}
                onClick={() => choose(item)}
                style={{
                  padding: "10px 12px",
                  cursor: item.disabled ? "default" : "pointer",
                  opacity: item.disabled ? 0.6 : 1,
                  background: idx === activeIdx ? "#1f2937" : "transparent",
                  borderBottom: "1px solid #232532",
                }}
              >
                <strong>{item.name}</strong>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default CitySearchBar;
